<template>
  <div>
    <div
      class="kb-form-fields kb-subkrewe-form"
      :class="{ 'is-compact': compact }"
    >
      <slot name="additional-fields" />

      <section class="kb-form-group">
        <h4 class="kb-label m-0">
          About
        </h4>

        <KbField
          field-id="subkrewe_name"
          label="SubKrewe name"
          :error="nameError"
        >
          <InputText
            id="subkrewe_name"
            v-model="form.values.name"
            size="small"
            fluid
            autocomplete="off"
            :invalid="Boolean(nameError)"
            @update:model-value="onNameInput"
            @blur="onNameBlur"
          />
        </KbField>

        <KbField
          field-id="subkrewe_description"
          label="Description"
          :error="form.errorFor('description')"
        >
          <Textarea
            id="subkrewe_description"
            v-model="form.values.description"
            size="small"
            fluid
            rows="3"
            auto-resize
            :invalid="Boolean(form.errorFor('description'))"
            @blur="onTextBlur('description')"
          />
        </KbField>

        <KbField
          field-id="subkrewe_estimated_members"
          label="Estimated members"
          hint="How many people you expect to march"
          :error="form.errorFor('estimated_members')"
          inline
        >
          <InputNumber
            v-model="form.values.estimated_members"
            input-id="subkrewe_estimated_members"
            size="small"
            input-class="w-20"
            :min="0"
            :use-grouping="false"
            :invalid="Boolean(form.errorFor('estimated_members'))"
            @blur="onTextBlur('estimated_members')"
          />
        </KbField>
      </section>

      <section class="kb-form-group">
        <h4 class="kb-label m-0">
          Contact &amp; links
        </h4>

        <KbField
          field-id="subkrewe_email"
          label="Public email"
          hint="Shown to members instead of the captains' email addresses"
          :error="form.errorFor('email')"
          optional
        >
          <InputText
            id="subkrewe_email"
            v-model="form.values.email"
            type="email"
            size="small"
            fluid
            placeholder="contact@mysubkrewe.com"
            autocomplete="off"
            :invalid="Boolean(form.errorFor('email'))"
            @blur="onTextBlur('email')"
          />
        </KbField>

        <SharedSubKreweLinkFields
          :key="currentSubkrewe.id"
          v-model="form.values.links"
          id-prefix="subkrewe_link"
          :error-for="field => form.errorFor(`links.${field}`)"
          @blur="field => onTextBlur(`links.${field}`)"
          @removed="onLinkRemoved"
          @pending="hasUnsavedLinkRows = $event"
        />
      </section>

      <section class="kb-form-group">
        <h4 class="kb-label m-0">
          Music &amp; admission
        </h4>

        <!-- Hidden for approved bands (unless admin): their music is fixed -->
        <KbField
          v-if="showMusicField"
          field-id="subkrewe_music"
          label="Music"
          :error="form.errorFor('music')"
        >
          <KbRadioList
            v-model="form.values.music"
            name="subkrewe_music"
            :options="musicOptions"
            :invalid="Boolean(form.errorFor('music'))"
            aria-labelledby="subkrewe_music-label"
            @change="onControlChange('music')"
          />
        </KbField>

        <div
          v-if="isApprovedBand && !isAdmin"
          class="kb-form-note"
        >
          <div>
            <div class="font-semibold text-kb-fg">
              Music: band (fixed)
            </div>
            <p class="text-kb-muted mt-1 mb-0">
              Your SubKrewe is registered as a band. This setting is locked after approval.
              All band members are exempt from dues.
            </p>
          </div>
        </div>

        <div
          v-if="isApprovedSubkrewe && !isApprovedBand && !isAdmin"
          class="kb-form-note kb-form-note-warn"
        >
          <div>
            <div class="font-semibold text-kb-gold">
              Note: music type restriction
            </div>
            <p class="text-kb-muted mt-1 mb-0">
              Once a SubKrewe is approved, the "We are a band" option is no longer available.
              Please contact an administrator if you need to change your SubKrewe to a band.
            </p>
          </div>
        </div>

        <div
          v-if="isApprovedSubkrewe && !isApprovedBand && isAdmin"
          class="kb-form-note"
        >
          <div>
            <div class="font-semibold text-kb-fg">
              Admin note: music type change
            </div>
            <p class="text-kb-muted mt-1 mb-0">
              As an administrator, you can change this approved SubKrewe to a band.
              Regular members cannot do this as band members are exempt from dues.
            </p>
          </div>
        </div>

        <div
          v-if="isApprovedBand && isAdmin"
          class="kb-form-note"
        >
          <div>
            <div class="font-semibold text-kb-fg">
              Admin note: editing a band
            </div>
            <p class="text-kb-muted mt-1 mb-0">
              As an administrator, you can change this band's music type. Regular members cannot
              change a band's music type after approval since band members are exempt from dues.
            </p>
          </div>
        </div>

        <KbField
          v-if="form.values.music === MUSIC_TYPES.OTHER"
          field-id="subkrewe_music_other"
          label="Music description"
          :error="form.errorFor('music_other')"
        >
          <Textarea
            id="subkrewe_music_other"
            v-model="form.values.music_other"
            size="small"
            fluid
            rows="2"
            auto-resize
            :invalid="Boolean(form.errorFor('music_other'))"
            @blur="onTextBlur('music_other')"
          />
        </KbField>

        <!-- Bands and sponsored SubKrewes are always closed; they get a note instead -->
        <KbField
          v-if="!admissionForcedClosed"
          field-id="subkrewe_admission_policy"
          label="Admission policy"
          :error="form.errorFor('admission_policy')"
        >
          <!-- Four options don't fit side by side on a phone -->
          <KbRadioList
            v-if="isNarrow"
            v-model="form.values.admission_policy"
            name="subkrewe_admission_policy"
            :options="admissionOptions"
            :invalid="Boolean(form.errorFor('admission_policy'))"
            aria-labelledby="subkrewe_admission_policy-label"
            @change="onControlChange('admission_policy')"
          />
          <KbChoice
            v-else
            v-model="form.values.admission_policy"
            :options="admissionOptions"
            :invalid="Boolean(form.errorFor('admission_policy'))"
            aria-labelledby="subkrewe_admission_policy-label"
            @change="onControlChange('admission_policy')"
          />
        </KbField>

        <div
          v-else-if="isBandMusicType"
          class="kb-form-note"
        >
          <div>
            <div class="font-semibold text-kb-fg">
              Note: band admissions and dues
            </div>
            <ul class="text-kb-muted mt-1 mb-0 list-disc pl-5">
              <li>Bands automatically have a <strong>Closed</strong> admission policy</li>
              <li>Members can not join bands unless you pre-approve them <strong>OR</strong> you make your band visible below so they can apply to join</li>
              <li>Band members are exempt from dues</li>
            </ul>
          </div>
        </div>

        <div
          v-else
          class="kb-form-note"
        >
          <div>
            <div class="font-semibold text-kb-fg">
              Note: sponsored SubKrewe admissions
            </div>
            <ul class="text-kb-muted mt-1 mb-0 list-disc pl-5">
              <li>Sponsored SubKrewes automatically have a <strong>Closed</strong> admission policy</li>
              <li>Members can not join sponsored SubKrewes unless you pre-approve them</li>
              <li>Sponsored SubKrewe members are exempt from dues</li>
            </ul>
          </div>
        </div>

        <KbField
          v-if="isBandMusicType"
          field-id="subkrewe_visible_in_browser"
          label="Make my band visible for members to join"
          inline
        >
          <ToggleSwitch
            v-model="form.values.visible_in_browser"
            input-id="subkrewe_visible_in_browser"
            @change="onControlChange('visible_in_browser')"
          />
        </KbField>

        <KbField
          v-if="needsRestrictions"
          field-id="subkrewe_admission_restrictions"
          label="Admission restrictions"
          :error="form.errorFor('admission_restrictions')"
        >
          <Textarea
            id="subkrewe_admission_restrictions"
            v-model="form.values.admission_restrictions"
            size="small"
            fluid
            rows="2"
            auto-resize
            :invalid="Boolean(form.errorFor('admission_restrictions'))"
            @blur="onTextBlur('admission_restrictions')"
          />
        </KbField>
      </section>

      <section class="kb-form-group">
        <h4 class="kb-label m-0">
          Contraptions &amp; parade
        </h4>

        <KbField
          field-id="subkrewe_contraption"
          label="Contraption"
          :error="form.errorFor('contraption')"
          inline
        >
          <KbChoice
            v-model="form.values.contraption"
            :options="CONTRAPTION_OPTIONS"
            :invalid="Boolean(form.errorFor('contraption'))"
            aria-labelledby="subkrewe_contraption-label"
            @change="onControlChange('contraption')"
          />
        </KbField>

        <template v-if="form.values.contraption === true">
          <KbField
            field-id="subkrewe_contraption_info"
            label="Contraption description"
            :error="form.errorFor('contraption_info')"
          >
            <Textarea
              id="subkrewe_contraption_info"
              v-model="form.values.contraption_info"
              size="small"
              fluid
              rows="2"
              auto-resize
              :invalid="Boolean(form.errorFor('contraption_info'))"
              @blur="onTextBlur('contraption_info')"
            />
          </KbField>

          <KbField
            field-id="subkrewe_contraption_count"
            label="Number of contraptions"
            hint="How many your SubKrewe will bring"
            :error="form.errorFor('contraption_count')"
            inline
          >
            <InputNumber
              v-model="form.values.contraption_count"
              input-id="subkrewe_contraption_count"
              size="small"
              input-class="w-20"
              :min="1"
              :use-grouping="false"
              :invalid="Boolean(form.errorFor('contraption_count'))"
              @blur="onTextBlur('contraption_count')"
            />
          </KbField>
        </template>

        <KbField
          field-id="subkrewe_rolling_next_parade"
          :label="`Rolling in the ${paradeYear} parade?`"
          :error="form.errorFor('rolling_next_parade')"
        >
          <KbChoice
            v-model="form.values.rolling_next_parade"
            :options="ROLLING_OPTIONS"
            :invalid="Boolean(form.errorFor('rolling_next_parade'))"
            aria-labelledby="subkrewe_rolling_next_parade-label"
            @change="onControlChange('rolling_next_parade')"
          />
        </KbField>
      </section>

      <section class="kb-form-group">
        <h4 class="kb-label m-0">
          Terms
        </h4>

        <!-- The explanation and disclaimer are for captains; the admin drawer
             shows just the field -->
        <div
          v-if="showTermsHelp"
          class="kb-form-note"
        >
          <div class="text-kb-muted">
            <div class="font-semibold text-kb-fg mb-1">
              SubKrewe terms &amp; conditions (optional)
            </div>
            <p class="mt-0 mb-2">
              If you require members to agree to additional terms (e.g. signing a waiver) before applying or joining,
              enter them below. Members will be shown these terms and must accept them before their application or join request
              is processed. Existing members will be prompted to accept any changes the next time they use the app.
            </p>
            <p class="m-0 text-xs italic text-kb-dim">
              <strong>Disclaimer:</strong> Terms &amp; Conditions you enter here are authored solely by your SubKrewe.
              The Intergalactic Krewe of Chewbacchus and KreWeBacchus make no representations as to the legality, accuracy, or enforceability of this text and
              are not responsible for the contents or consequences of any SubKrewe-specific Terms &amp; Conditions. You are responsible for ensuring your terms
              comply with applicable law. Consult your own legal counsel if you are unsure.
            </p>
          </div>
        </div>

        <KbField
          field-id="subkrewe_terms_and_conditions"
          label="Terms & conditions"
          :error="form.errorFor('terms_and_conditions')"
          optional
        >
          <Textarea
            id="subkrewe_terms_and_conditions"
            v-model="form.values.terms_and_conditions"
            size="small"
            fluid
            rows="8"
            placeholder="Leave blank if your SubKrewe has no additional terms."
            :invalid="Boolean(form.errorFor('terms_and_conditions'))"
            @blur="onTextBlur('terms_and_conditions')"
          />
        </KbField>
      </section>
    </div>

    <!-- Similar name warning -->
    <Dialog
      v-model:visible="dialogVisible"
      header="Similar SubKrewe names found"
      modal
      :closable="false"
      :close-on-escape="false"
      :style="{ width: '28rem' }"
      :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
    >
      <p class="kb-dialog-lead">
        We found existing SubKrewes with similar names:
      </p>
      <ul class="kb-similar-names">
        <li
          v-for="(sk, index) in similarSubkrewes"
          :key="sk.id || index"
        >
          {{ sk.name }}
        </li>
      </ul>
      <p class="kb-dialog-lead">
        Are you sure your SubKrewe is different from these?
      </p>

      <template #footer>
        <Button
          label="Change name"
          icon="pi pi-pencil"
          text
          severity="secondary"
          @click="changeName"
        />
        <Button
          label="Continue with this name"
          icon="pi pi-check"
          @click="confirmName"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { z } from 'zod'
import { ADMISSION_POLICIES, getAdmissionPolicyRadioItems } from '~/shared/utils/admissionPolicies'
import { memberIsAdmin } from '~/shared/utils/memberUtils'
import { MUSIC_TYPES, getMusicTypeRadioItems } from '~/shared/utils/musicTypes'
import type { SubKreweLinkField } from '~/shared/utils/subkreweLinks.js'
import { SUBKREWE_LINK_FIELDS } from '~/shared/utils/subkreweLinks.js'
import { getRollingStatusForYearOrUnknown } from '~/shared/utils/subkreweRolling.js'
import { optionalEmailField, optionalText, requiredText } from '~/utils/formSchemas'
import { subkreweLinkValues, subkreweLinksSchema } from '~/utils/subkreweLinkRule'

interface Props {
  data: SubKrewe
  /** Tighter spacing, for the admin drawer */
  compact?: boolean
  /** The explanation and disclaimer above the terms field; the admin drawer leaves them out */
  showTermsHelp?: boolean
}

const props = withDefaults(defineProps<Props>(), { showTermsHelp: true })
const emit = defineEmits(['update:data', 'update:title', 'saved', 'nameValidated'])

const subkrewesStore = useSubKrewesStore()
const toast = useToast()
const { paradeYear } = useParadeYear()
const nameValidator = useSubKreweNameValidator()
const currentMember = ref<Member | null>(null)

onMounted(async () => {
  try {
    currentMember.value = await useCurrentMember()
  }
  catch (error) {
    console.error('Error fetching current member:', error)
  }
})

const isAdmin = computed(() => memberIsAdmin(currentMember.value))

// The saved SubKrewe: the live store copy when there is one
const currentSubkrewe = computed(() => {
  if (props.data.id) {
    const storeSubkrewe = subkrewesStore.subkrewes[`subkrewes/${props.data.id}`]
    return storeSubkrewe || props.data
  }
  return props.data
})

const isSponsoredSubkrewe = computed(() => currentSubkrewe.value?.sponsored === true)
const isApprovedSubkrewe = computed(() => currentSubkrewe.value?.approved === 'Yes')
const isApprovedBand = computed(() => isApprovedSubkrewe.value && currentSubkrewe.value?.music === MUSIC_TYPES.BAND)

// Approved bands can't change their music, unless an admin is editing
const showMusicField = computed(() => isAdmin.value || !isApprovedBand.value)

// ---- Form ----

const schema = z.object({
  name: requiredText('SubKrewe name is required'),
  description: requiredText('Description is required', 500),
  email: optionalEmailField,
  links: subkreweLinksSchema,
  estimated_members: z.number().int('Whole numbers only').min(0, 'Can\'t be negative').nullable(),
  music: z.string().min(1, 'Choose the kind of music'),
  music_other: z.string(),
  admission_policy: z.string(),
  admission_restrictions: z.string(),
  visible_in_browser: z.boolean(),
  contraption: z.boolean().nullable(),
  contraption_info: z.string(),
  contraption_count: z.number().nullable(),
  rolling_next_parade: z.enum(['Yes', 'No', 'Unknown'], {
    error: 'Please indicate if your SubKrewe will be rolling in the next parade',
  }),
  terms_and_conditions: optionalText(10000),
}).superRefine((values, ctx) => {
  const require = (missing: boolean, field: string, message: string) => {
    if (missing) ctx.addIssue({ code: 'custom', path: [field], message })
  }
  require(values.estimated_members === null, 'estimated_members', 'Enter how many people you expect to march')
  require(values.contraption === null, 'contraption', 'Choose yes or no')
  require(values.music === MUSIC_TYPES.OTHER && !values.music_other.trim(), 'music_other', 'Describe your music')

  // The policy only shows (and only matters) when it isn't forced closed
  const policyShown = values.music !== MUSIC_TYPES.BAND && !isSponsoredSubkrewe.value
  require(policyShown && !values.admission_policy, 'admission_policy', 'Choose an admission policy')
  require(
    policyShown && values.admission_policy === ADMISSION_POLICIES.OPEN_WITH_RESTRICTIONS && !values.admission_restrictions.trim(),
    'admission_restrictions',
    'Describe the restrictions',
  )

  if (values.contraption === true) {
    require(!values.contraption_info.trim(), 'contraption_info', 'Describe your contraption')
    require(values.contraption_count === null, 'contraption_count', 'Enter how many contraptions')
    require(values.contraption_count !== null && values.contraption_count < 1, 'contraption_count', 'At least 1')
  }
})

type FormValues = z.input<typeof schema>

// Counts written by the old form could be strings ("12")
const asCount = (value: unknown): number | null => {
  const count = typeof value === 'string' && value.trim() ? Number(value) : value
  return typeof count === 'number' && Number.isFinite(count) ? count : null
}

function valuesFrom(subkrewe: SubKrewe): FormValues {
  return {
    name: subkrewe.name || '',
    description: subkrewe.description || '',
    email: subkrewe.email || '',
    links: subkreweLinkValues(subkrewe),
    estimated_members: asCount(subkrewe.estimated_members),
    music: subkrewe.music || '',
    music_other: subkrewe.music_other || '',
    admission_policy: subkrewe.admission_policy || '',
    admission_restrictions: subkrewe.admission_restrictions || '',
    visible_in_browser: Boolean(subkrewe.visible_in_browser),
    contraption: typeof subkrewe.contraption === 'boolean' ? subkrewe.contraption : null,
    contraption_info: subkrewe.contraption_info || '',
    contraption_count: asCount(subkrewe.contraption_count) ?? 1,
    // The form holds the flat value for this parade year; the store writes it
    // into the per-year map
    rolling_next_parade: getRollingStatusForYearOrUnknown(subkrewe, paradeYear.value),
    terms_and_conditions: subkrewe.terms_and_conditions || '',
  }
}

const form = useZodForm(schema, valuesFrom(currentSubkrewe.value))

const isBandMusicType = computed(() => form.values.music === MUSIC_TYPES.BAND)
const admissionForcedClosed = computed(() => isBandMusicType.value || isSponsoredSubkrewe.value)
const needsRestrictions = computed(() =>
  !admissionForcedClosed.value && form.values.admission_policy === ADMISSION_POLICIES.OPEN_WITH_RESTRICTIONS,
)

// True while a link row is open with nothing in it: the captain is about to
// type there, so the form holds off taking in store updates
const hasUnsavedLinkRows = ref(false)

// Take in the saved SubKrewe when it changes (a save landing, another
// admin), unless someone is mid-edit here
const savedValues = computed(() => valuesFrom(currentSubkrewe.value))

function syncFromStore() {
  if (!form.isDirty.value && !hasUnsavedLinkRows.value) form.reset(savedValues.value)
}

watch(savedValues, syncFromStore)

// ---- Options ----

type Tone = 'ok' | 'warn' | 'orange' | 'alert'

const ADMISSION_TONES: Record<string, Tone> = {
  [ADMISSION_POLICIES.OPEN]: 'ok',
  [ADMISSION_POLICIES.OPEN_WITH_RESTRICTIONS]: 'warn',
  [ADMISSION_POLICIES.BY_REFERRAL]: 'orange',
  [ADMISSION_POLICIES.CLOSED]: 'alert',
}

const admissionOptions = getAdmissionPolicyRadioItems().map(item => ({ ...item, tone: ADMISSION_TONES[item.value] }))

const CONTRAPTION_OPTIONS: { label: string, value: boolean, tone: Tone }[] = [
  { label: 'Yes', value: true, tone: 'ok' },
  { label: 'No', value: false, tone: 'alert' },
]

const ROLLING_OPTIONS: { label: string, value: FormValues['rolling_next_parade'], tone: Tone }[] = [
  { label: 'Yes', value: 'Yes', tone: 'ok' },
  { label: 'No', value: 'No', tone: 'alert' },
  { label: 'Not sure yet', value: 'Unknown', tone: 'warn' },
]

// Once approved, only an admin can make a SubKrewe a band. The option stays
// in the list, disabled, so the list doesn't shift.
const musicOptions = computed(() => getMusicTypeRadioItems().map(item => ({
  label: item.label,
  value: item.value,
  hint: item.description,
  disabled: item.value === MUSIC_TYPES.BAND && !isAdmin.value && isApprovedSubkrewe.value,
})))

const isNarrow = useMediaQuery('(max-width: 639px)')

// ---- Name checks ----

const NAME_TAKEN = 'This name is already taken. Please choose a different name.'

const savedName = computed(() => currentSubkrewe.value.name || '')
// The typed name belongs to another SubKrewe
const nameTaken = ref(false)
// False while the name is taken, or sounds like another one and the captain
// hasn't confirmed it yet
const nameIsValid = ref(true)
// A similar-sounding name the captain chose to keep
const confirmedName = ref('')
// The similar-name check running after the name field was left; saves wait for it
let nameCheck: Promise<boolean> | null = null

// Another SubKrewe: start over
watch(() => props.data.id, () => {
  form.reset(valuesFrom(currentSubkrewe.value))
  nameTaken.value = false
  nameIsValid.value = true
  confirmedName.value = ''
})

const nameError = computed(() => form.errorFor('name') ?? (nameTaken.value ? NAME_TAKEN : undefined))

const dialogVisible = computed({
  get: () => nameValidator.showSimilarNameDialog.value,
  set: (value) => { nameValidator.showSimilarNameDialog.value = value },
})

const similarSubkrewes = computed(() =>
  Array.isArray(nameValidator.similarSubkrewes.value) ? nameValidator.similarSubkrewes.value : [],
)

// While typing: is the name another SubKrewe's?
async function onNameInput() {
  const name = form.values.name.trim()

  if (name.length < 3 || name === savedName.value) {
    nameTaken.value = false
    nameIsValid.value = true
    return
  }
  if (name === confirmedName.value) return

  const match = await nameValidator.checkExactNameMatch(name)
  // Typed on meanwhile: that newer check decides
  if (form.values.name.trim() !== name) return

  const taken = Boolean(match && match.id !== currentSubkrewe.value.id)
  nameTaken.value = taken
  nameIsValid.value = !taken
  emit('nameValidated', !taken)
}

// Leaving the field: a new name that sounds like another SubKrewe's asks the
// captain first. Returns whether to go ahead and save.
async function checkSimilarNames(): Promise<boolean> {
  const name = form.values.name.trim()
  if (name.length < 3 || name === savedName.value || name === confirmedName.value) return true

  await onNameInput()
  // Taken: the save refuses and says so
  if (nameTaken.value) return true

  try {
    const similar = (await nameValidator.checkForSimilarNames(name) || [])
      .filter(sk => sk.id !== currentSubkrewe.value.id)

    if (similar.length > 0) {
      nameValidator.similarSubkrewes.value = similar
      nameValidator.showSimilarNameDialog.value = true
      nameIsValid.value = false
      // Saved once the captain decides
      return false
    }
  }
  catch (error) {
    console.error('Error checking for similar names:', error)
  }

  nameValidator.nameValidated.value = true
  nameIsValid.value = true
  emit('nameValidated', true)
  return true
}

async function onNameBlur() {
  form.touch('name')
  nameCheck = checkSimilarNames()
  const goAhead = await nameCheck
  nameCheck = null
  if (goAhead) await save()
}

function changeName() {
  nameValidator.showSimilarNameDialog.value = false
  form.values.name = ''
  nameTaken.value = false

  setTimeout(() => document.getElementById('subkrewe_name')?.focus(), 100)
}

function confirmName() {
  nameValidator.showSimilarNameDialog.value = false
  nameValidator.nameValidated.value = true
  nameIsValid.value = true
  confirmedName.value = form.values.name.trim()

  emit('nameValidated', true)
  save()
}

// ---- Auto-save ----

// Text fields save when left
async function onTextBlur(field: string) {
  // InputNumber writes its value only after it reports the blur
  await nextTick()
  form.touch(field)
  await save()
}

// Choices and switches save right away
function onControlChange(field: string) {
  form.touch(field)
  save()
}

// A field that appears because of another one is required with it: picking
// "Other" music needs a description before either saves
const DEPENDENT_FIELDS: Record<string, string[]> = {
  music: ['music_other', 'admission_policy', 'admission_restrictions'],
  admission_policy: ['admission_restrictions'],
  contraption: ['contraption_info', 'contraption_count'],
}

// Removing a row empties its value; clear the stored link if there was one
async function onLinkRemoved(field: SubKreweLinkField) {
  if (!subkreweLinkValues(currentSubkrewe.value)[field]) return
  await save({ summary: 'Removed', detail: 'Link removed' })
}

const saving = ref(false)
let saveAgain = false

/**
 * Save the fields that changed, when they (and the fields that come with
 * them) are valid. Other fields with old, incomplete data don't block it.
 */
async function save(successMessage?: { summary: string, detail: string }) {
  if (nameCheck) await nameCheck
  if (saving.value) {
    // One save at a time; this one runs when the current one is done
    saveAgain = true
    return
  }
  if (!form.isDirty.value) return

  // Bands and sponsored SubKrewes are always closed (their policy is hidden)
  if (admissionForcedClosed.value && form.values.admission_policy !== ADMISSION_POLICIES.CLOSED) {
    form.values.admission_policy = ADMISSION_POLICIES.CLOSED
  }

  const subkrewe = currentSubkrewe.value
  const changed = form.changedValues()
  const updates: Record<string, unknown> = {}
  const fields = new Set<string>()

  for (const [key, value] of Object.entries(changed)) {
    if (key === 'links') {
      // The links save as the SubKrewe's own fields, only the ones that differ
      const stored = subkreweLinkValues(subkrewe)
      for (const field of SUBKREWE_LINK_FIELDS) {
        if (form.values.links[field] !== stored[field]) {
          updates[field] = form.values.links[field]
          fields.add(`links.${field}`)
        }
      }
      continue
    }
    updates[key] = value
    fields.add(key)
    for (const dependent of DEPENDENT_FIELDS[key] ?? []) fields.add(dependent)
  }

  for (const field of fields) form.touched[field] = true
  if (!form.validateFields(...fields)) return

  if ('name' in updates) {
    // Waiting on the captain's answer to the similar-name question
    if (nameValidator.showSimilarNameDialog.value) return
    if (!nameIsValid.value) {
      toast.add({
        severity: 'error',
        summary: 'Name Validation Failed',
        detail: 'The SubKrewe name is already taken or invalid. Please choose a different name.',
        life: 5000,
      })
      return
    }
  }

  // Contraptions turned on: the count shown with them saves too, unless one is stored
  if (updates.contraption === true && subkrewe.contraption_count == null) {
    updates.contraption_count = form.values.contraption_count
  }

  const cleanUpdates = Object.fromEntries(
    Object.entries(updates).filter(([, value]) => value !== null && value !== undefined),
  ) as Partial<SubKrewe>

  if (Object.keys(cleanUpdates).length === 0) {
    form.markSaved()
    return
  }

  const updatedSubkrewe: SubKrewe = { ...subkrewe, ...cleanUpdates }

  if (typeof cleanUpdates.name === 'string' && cleanUpdates.name.trim() !== subkrewe.name) {
    const title = cleanUpdates.name.trim() || 'SubKrewe Details'
    emit('update:title', title)
    useDrawer().updateDrawerTitle(title)
  }

  saving.value = true
  const subkreweId = subkrewe.id
  const sentValues = JSON.stringify(form.values)

  try {
    // The saved SubKrewe, not the edited one: the store compares against it
    // (the terms, to know whether members must accept them again)
    const success = await subkrewesStore.updateSubKrewe(subkrewe, cleanUpdates)

    // Moved on to another SubKrewe meanwhile
    if (subkreweId !== props.data.id) return

    if (success) {
      // Only what was sent counts as saved; typing that happened meanwhile
      // stays dirty and saves on the next round
      if (JSON.stringify(form.values) === sentValues) {
        form.markSaved()
        // Show what the store kept (links as full URLs, text trimmed)
        syncFromStore()
      }

      toast.add({
        severity: 'success',
        summary: successMessage?.summary ?? 'Saved',
        detail: successMessage?.detail ?? `SubKrewe ${updatedSubkrewe.name} updated`,
        life: 2000,
      })

      emit('saved', updatedSubkrewe)
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update SubKrewe information',
        life: 3000,
      })
    }
  }
  catch (error) {
    console.error('Error saving SubKrewe:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
  finally {
    saving.value = false
    if (saveAgain) {
      saveAgain = false
      save()
    }
  }
}

onUnmounted(() => {
  saveAgain = false
})
</script>

<style scoped>
.kb-subkrewe-form.is-compact,
.kb-subkrewe-form.is-compact > .kb-form-group {
  gap: 0.75rem;
}

.kb-subkrewe-form.is-compact > .kb-form-group + .kb-form-group {
  padding-top: 0.75rem;
}

.kb-similar-names {
  margin: 0.75rem 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--kb-line);
  border-radius: 4px;
  font-size: 0.875rem;
  color: var(--kb-fg);
}

.kb-similar-names > li {
  padding: 0.5rem 0.75rem;
}

.kb-similar-names > li + li {
  border-top: 1px solid var(--kb-line);
}
</style>
